import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, from, lastValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, toArray } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { TelegramMessengerService } from '@src/app/modules/telegram';
import { TelegramWebAppService } from '@src/app/modules/telegram-web-app';
import { EnvService } from '@src/app/modules/env';
import { normalizeUserDataForUI, sortingUsers } from '@src/utils';
import { AuthUserService, SessionService } from '@src/app/modules/auth';
import {
  AddUser,
  Id,
  UserService as ApiUserService,
  UserProfileWithJobTitlesFull,
  UsersSearchParameters,
  ContactsForUserService,
  ContactForUserView,
  UserRegistrationService,
  GetActualRegistrationFormsForAssociationResponseDto,
  UserDeleteService,
  ReplaceUserContactsRequestDto,
} from '@src/api';
import { ChatMemberModel, UserResponseUI, UserSortType, UserUI } from '@src/models';
import { InternalMessengerService } from '@src/app/modules/internal-messenger';
import { SearchCitiesService } from '@src/app/modules/search-cities/services';
import { UsersByOrganisationStoreService } from '@src/app/modules/user-info';

import { AlertService, BaseSearchService, SettingService } from '../services';
import { APP_CONFIG } from '../config';

@Injectable({
  providedIn: 'root',
})
export class UserService implements BaseSearchService<UsersSearchParameters, UserSortType, UserResponseUI> {
  private readonly authUserService = inject(AuthUserService);
  private readonly settingService = inject(SettingService);
  private readonly userService = inject(ApiUserService);
  private readonly userDeleteService = inject(UserDeleteService);
  private readonly userRegistrationService = inject(UserRegistrationService);
  private readonly contactsForUserService = inject(ContactsForUserService);
  private readonly messengerService = inject(TelegramMessengerService);
  private readonly router = inject(Router);
  private readonly env = inject(EnvService);
  private readonly alertService = inject(AlertService);
  private readonly sessionService = inject(SessionService);
  private readonly translateService = inject(TranslateService);
  private readonly telegramWebAppService = inject(TelegramWebAppService);
  private readonly internalMessengerService = inject(InternalMessengerService);
  private readonly searchCitiesService = inject(SearchCitiesService);
  private readonly usersByOrganisationStore = inject(UsersByOrganisationStoreService);

  authUser?: UserUI;
  authUser$ = new BehaviorSubject<UserUI | undefined>(undefined);
  user$ = new BehaviorSubject<UserUI | null>(null);

  listOfProfiles$ = new BehaviorSubject<UserUI[]>([{}]);
  membersOfEvent$ = new BehaviorSubject<UserUI[]>([]);
  membersOfPoll$ = new BehaviorSubject<UserUI[] | null>(null);
  membersOfCommittee$ = new BehaviorSubject<UserUI[] | null>(null);
  unregistredUsers$ = new BehaviorSubject<UserUI[] | null>(null);
  moderationUsers$ = new BehaviorSubject<GetActualRegistrationFormsForAssociationResponseDto[] | null>(null);
  usersTelegramIds$ = new BehaviorSubject<number[]>([]);
  loadUsers$ = new BehaviorSubject<number[] | null>(null);
  getUserById$ = new BehaviorSubject<string | number | null>(null);

  filter$ = new BehaviorSubject<UsersSearchParameters | null>(null);
  sort$ = new BehaviorSubject<UserSortType | null>('fio');
  pagination$ = new BehaviorSubject<number>(0);

  constructor() {
    this.authUser$ = this.authUserService.authUser$;
    this.authUser$.subscribe(user => {
      this.authUser = user;

      // TODO: Текущая логика автоподписок не будет работать, если пользователь пошел в телегу внутри приложения.
      // Параметр isFirstSignIn не подходит для автоподписок - нужно менять логику
      // Параметр isFirstSignIn нужен только для отображения плашек при первом входе
      if (user?.isFirstSignIn && user?.parentOrganisationId && this.sessionService.isLoggedTelegram()) {
        this.settingService.initAutoSubscriptions(user);
        // TODO: это временное решение бага #9186
        this.sessionService.changeToken(user.parentOrganisationId).subscribe();
      }
    });

    this.getUserObservable().subscribe();
  }

  async getAuthorizedUser(): Promise<UserUI | undefined> {
    return this.authUser;
  }

  loadUsers(membersTelegramIds: number[] | null): void {
    this.loadUsers$.next(membersTelegramIds);
  }

  getUsersObservable(): Observable<{ users: UserUI[] | null; telegramUsers: ChatMemberModel[] | null } | null> {
    let membersTelegramIdsSaved: number[] | null = null;
    let usersSaved: UserUI[] | null = null;
    return this.loadUsers$.pipe(filter(membersTelegramIds => membersTelegramIds !== null)).pipe(
      switchMap(membersTelegramIds => {
        membersTelegramIdsSaved = membersTelegramIds;
        if (!!membersTelegramIds) {
          return this.getUsersData(membersTelegramIds);
        } else {
          return of(null);
        }
      }),
      switchMap(users => {
        if (!!users && !!membersTelegramIdsSaved) {
          usersSaved = users;
          return from(membersTelegramIdsSaved).pipe(
            mergeMap(membersTelegramId => {
              return this.messengerService.api.getUserObservable(membersTelegramId);
            }),
            toArray(),
          );
        } else {
          return of(null);
        }
      }),
      map(telegramUsers => {
        return {
          users: usersSaved,
          telegramUsers: telegramUsers,
        };
      }),
    );
  }

  getUsersData(ids: (number | string)[]): Observable<UserUI[]> {
    if (ids.length) {
      const id = ids[0];
      if (typeof id === 'number') {
        return this.userService.getListOfProfiles(ids as number[]).pipe(
          catchError(() => {
            // TODO: добавить локализацию
            this.alertService.error('Ошибка загрузки списка пользователей');
            return of(null);
          }),
          map(users => (users || []).map(user => normalizeUserDataForUI(user))),
        );
      } else if (typeof id === 'string') {
        return this.userService.getUserProfilesByIds(ids as string[]).pipe(
          catchError(() => {
            // TODO: добавить локализацию
            this.alertService.error('Ошибка загрузки списка пользователей');
            return of(null);
          }),
          map(users => (users || []).map(user => normalizeUserDataForUI(user))),
        );
      }
    }

    return of([]);
  }

  getUsersTelegramIds(ids: number[]): void {
    this.userService
      .getListOfProfiles(ids)
      .pipe(
        catchError(err => {
          // TODO show error notification
          return throwError(() => err);
        }),
        map(users => users.map(user => user.telegramId!)),
      )
      .subscribe(telegramIds => this.usersTelegramIds$.next(telegramIds));
  }

  private getUser(id: number | string | null, callback?: (value: boolean) => void): Observable<UserUI | null> {
    if (typeof id === 'number') {
      return this.getUsersByTelegramId(id, callback);
    } else if (typeof id === 'string') {
      return this.getUserById(id, callback);
    } else {
      return of(null);
    }
  }

  loadUser(userId: string | number): void {
    this.getUserById$.next(userId);
  }

  getUserObservable(): Observable<UserUI | null> {
    return this.getUserById$.pipe(
      filter(userId => !!userId),
      switchMap(userId => {
        return this.getUser(userId).pipe(
          catchError(() => {
            // TODO: добавить локализацию
            this.alertService.error('Ошибка загрузки пользователя');
            return of(null);
          }),
          switchMap(user => {
            if (user && user.id === this.authUser$.value?.id && user.cityId) {
              return this.searchCitiesService.getCityById(user.cityId).pipe(
                map(city => {
                  return {
                    ...user,
                    city,
                  };
                }),
              );
            } else {
              return of(user);
            }
          }),
          map(userUI => {
            return userUI;
          }),
        );
      }),
      map(result => {
        this.user$.next(result);
        return result;
      }),
    );
  }

  getUserData(id: string): Observable<UserUI> {
    return this.userService.getUserProfilesByIds([id]).pipe(
      catchError(err => {
        // TODO show error notification
        return throwError(() => err);
      }),
      map(user => normalizeUserDataForUI(user[0])),
    );
  }

  getListOfProfiles(ids: (number | string)[]): void {
    if (ids.length) {
      const id = ids[0];
      if (typeof id === 'number') {
        this.getUsersByTelegramIds(ids as number[]);
      } else if (typeof id === 'string') {
        this.getUsersByIds(ids as string[]);
      }
    } else {
      this.resetListOfProfiles();
    }
  }

  getUnregistredUsers() {
    this.userService
      .newcomersList()
      .pipe(
        catchError(err => {
          this.showError(this.translateService.instant('components.newUsers.alerts.errors.newcomersList'), err);
          return of([]);
        }),
        map(users => sortingUsers(users.map(user => normalizeUserDataForUI(user)))),
      )
      .subscribe(users => this.unregistredUsers$.next(users));
  }

  getModerationUsers() {
    this.userRegistrationService
      .getActualRegistrationFormsForAssociation()
      .pipe(
        catchError(err => {
          this.showError(this.translateService.instant('components.newUsers.alerts.errors.moderationUsersList'), err);
          return [];
        }),
      )
      .subscribe(users => this.moderationUsers$.next(users));
  }

  getUnregistredUsersForOrganisation(organisationId: string) {
    this.userService
      .getUnregistredUsersForOrganisationWithId(organisationId)
      .pipe(
        catchError(err => {
          // TODO show error notification
          return throwError(() => err);
        }),
        map(users => sortingUsers(users.map(user => normalizeUserDataForUI(user)))),
      )
      .subscribe(users => this.unregistredUsers$.next(users));
  }

  async getUsernameByTelegramId(telegramId: number) {
    return await lastValueFrom(
      this.userService.usernameByTelegramId(telegramId).pipe(
        catchError(err => {
          // TODO show error notification
          return throwError(() => err);
        }),
      ),
    );
  }

  async getUserMutualChats(telegramId: number): Promise<number[]> {
    return await lastValueFrom(
      this.userService.userMutalChats(telegramId).pipe(
        catchError(err => {
          // TODO show error notification
          return throwError(() => err);
        }),
      ),
    );
  }

  getActiveUsers(associationId?: string, organisationId?: string, sendToBot: boolean = false) {
    return this.userService.getActiveUsers(associationId, organisationId, sendToBot, 'response');
  }

  async getUserFullName(telegramId: number) {
    const profiles = await lastValueFrom(
      this.userService.getListOfProfiles([telegramId]).pipe(
        catchError(err => {
          // TODO show error notification
          return throwError(() => err);
        }),
        map(profiles => sortingUsers(profiles.map(profile => normalizeUserDataForUI(profile)))),
      ),
    );

    if (profiles?.length) {
      return profiles[0].fullName!;
    }

    return await this.messengerService.getUserFullName(telegramId);
  }

  getUserContacts(id: string) {
    return this.contactsForUserService.contactsForUserList(id).pipe(
      catchError(err => {
        // TODO: добавить локализацию
        this.alertService.error('Ошибка получения дополнительных полей');
        return throwError(() => err);
      }),
      map(contacts => this.sortingUserContacts(contacts)),
    );
  }

  async getUserContactsData(id: string): Promise<ContactForUserView[]> {
    return await lastValueFrom(this.getUserContacts(id));
  }

  async replaceUserContacts(data: ReplaceUserContactsRequestDto) {
    return await lastValueFrom(
      this.contactsForUserService.replaceUserContacts(data).pipe(
        catchError(err => {
          // TODO: добавить локализацию
          this.alertService.error('Ошибка при добавлении дополнительных полей');
          return throwError(() => err);
        }),
      ),
    );
  }

  getUsersByOrganisationId(organisationId: string) {
    return this.userService.getUsersByOrganisationId(organisationId).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
      map(profiles => sortingUsers(profiles.map(profile => normalizeUserDataForUI(profile)))),
      tap(users => {
        this.usersByOrganisationStore.setData(organisationId, users);
      }),
    );
  }

  getUsersByCommitteeId(committeeId: string, callback?: (value: boolean) => void): void {
    callback?.(true);

    this.userService
      .usersByCommitteeId(committeeId)
      .pipe(
        catchError(err => {
          // TODO show error notification
          callback?.(false);
          return throwError(() => err);
        }),
        map(profiles => sortingUsers(profiles.map(profile => normalizeUserDataForUI(profile)))),
      )
      .subscribe(profiles => {
        this.membersOfCommittee$.next(profiles);
        callback?.(false);
      });
  }

  getUsersByEventId(eventId: string): void {
    this.userService
      .usersByEventId(eventId)
      .pipe(
        catchError(err => {
          // TODO show error notification
          return throwError(() => err);
        }),
        map(profiles => sortingUsers(profiles.map(profile => normalizeUserDataForUI(profile)))),
      )
      .subscribe(profiles => this.membersOfEvent$.next(profiles));
  }

  getUsersValueByEventId(eventId: string): Observable<UserUI[]> {
    return this.userService.usersByEventId(eventId).pipe(
      catchError(err => {
        // TODO show error notification
        return throwError(() => err);
      }),
      map(profiles => sortingUsers(profiles.map(profile => normalizeUserDataForUI(profile)))),
    );
  }

  getUsersByPollId(pollId: string): void {
    this.userService
      .usersByPollId(pollId)
      .pipe(
        catchError(err => {
          // TODO show error notification
          return throwError(() => err);
        }),
        map(profiles => sortingUsers(profiles.map(profile => normalizeUserDataForUI(profile)))),
      )
      .subscribe(profiles => this.membersOfPoll$.next(profiles));
  }

  getUserByPhone(phone: string) {
    return this.userService.userProfilesByPhone(phone).pipe(
      catchError(err => {
        // TODO show error notification
        return throwError(() => err);
      }),
      map(profile => normalizeUserDataForUI(profile as UserProfileWithJobTitlesFull)),
    );
  }

  getUserByEmail(email: string) {
    return this.userService.userProfilesByEmail(email).pipe(
      catchError(err => {
        // TODO show error notification
        return throwError(() => err);
      }),
      map(profile => normalizeUserDataForUI(profile as UserProfileWithJobTitlesFull)),
    );
  }

  createUser(data: AddUser): Observable<Id> {
    return this.userService.addUser(data).pipe(
      catchError(err => {
        this.sendError(err);
        return throwError(() => err);
      }),
    );
  }

  // TODO: возможно лучше добавить метод загрузки фото к организации
  editUser(data: UserUI): Observable<Id> {
    return this.userService.editUser(data).pipe(
      catchError(err => {
        this.sendError(err);

        return throwError(() => err);
      }),
      tap(() => {
        if (data.id === this.authUser?.id) {
          this.authUserService.updateAuthUser(data);
        }
      }),
    );
  }

  excludeUser(userId: string, organisationId: string) {
    return this.userDeleteService.excludeUserFromOrganisation(userId, organisationId).pipe(
      catchError(err => {
        this.sendError(err);

        return throwError(() => err);
      }),
    );
  }

  deleteUser(userId: string): Observable<void> {
    return this.userDeleteService.deleteUser(userId).pipe(
      catchError(err => {
        this.sendError(err);
        return throwError(() => err);
      }),
    );
  }

  deleteMyProfile() {
    return this.userDeleteService.deleteMyProfile().pipe(
      catchError(err => {
        this.sendError(err);
        return throwError(() => err);
      }),
    );
  }

  search(pagination: number, filter: UsersSearchParameters, sortBy: UserSortType): Observable<UserResponseUI> {
    const top = APP_CONFIG.loadingLimit.users;
    const start = pagination * APP_CONFIG.loadingLimit.users;

    return this.userService.searchUsers(top, start, undefined, undefined, sortBy, filter).pipe(
      map(response => {
        response.result = response.result?.map(item => normalizeUserDataForUI(item));

        return response;
      }),
      catchError(err => {
        // TODO handle error
        return throwError(() => err);
      }),
    );
  }

  searchUsers(filter: UsersSearchParameters, sortBy: UserSortType = 'fio'): Observable<UserUI[]> {
    return this.userService.searchUsers(3000, 0, undefined, undefined, sortBy, filter).pipe(
      map(response => (response.result ? response.result.map(user => normalizeUserDataForUI(user)) : [])),
      catchError(err => {
        // TODO handle error
        return throwError(() => err);
      }),
    );
  }

  getAllContactsForUserDescriptionsForOrganisations(): Observable<Array<string>> {
    return this.contactsForUserService.allContactsForUserDescriptionsForOrganisations().pipe(
      catchError(errorMessage => {
        this.showError(
          this.translateService.instant(
            'components.users.alerts.errors.allContactsForUserDescriptionsForOrganisations',
          ),
          errorMessage,
        );
        return of([]);
      }),
    );
  }

  getUnionsChatsIds(tgChatsIds: number[]) {
    return this.userService.filter(tgChatsIds).pipe(
      catchError(err => {
        // TODO handle error
        return throwError(() => err);
      }),
    );
  }

  resetUser(): void {
    this.user$.next(null);
  }

  resetListOfProfiles(): void {
    this.listOfProfiles$.next([{}]);
  }

  resetUnregistredUsers(): void {
    this.unregistredUsers$.next(null);
  }

  resetModerationUsers(): void {
    this.moderationUsers$.next(null);
  }

  resetMembersOfEvent(): void {
    this.membersOfEvent$.next([]);
  }

  resetMembersOfPoll(): void {
    this.membersOfPoll$.next(null);
  }

  resetMembersOfCommittee(): void {
    this.membersOfCommittee$.next(null);
  }

  resetAll(): void {
    this.resetUser();
    this.resetListOfProfiles();
    this.resetUnregistredUsers();
    this.resetModerationUsers();
    this.resetMembersOfEvent();
    this.resetMembersOfPoll();
    this.resetMembersOfCommittee();
  }

  getSearchUsers(users: UserUI[], searchQuery: string): UserUI[] {
    const query = searchQuery.toLowerCase();
    const filteredUsers = users.filter(user => {
      const firstName = user.firstName?.toLowerCase();
      const middleName = user.middleName?.toLowerCase();
      const lastName = user.lastName?.toLowerCase();
      const fml = [firstName, middleName, lastName].join(' ');
      const flm = [firstName, lastName, middleName].join(' ');
      const mfl = [middleName, firstName, lastName].join(' ');
      const mlf = [middleName, lastName, firstName].join(' ');
      const lfm = [lastName, firstName, middleName].join(' ');
      const lmf = [lastName, middleName, firstName].join(' ');

      return (
        fml.includes(query) ||
        flm.includes(query) ||
        mfl.includes(query) ||
        mlf.includes(query) ||
        lfm.includes(query) ||
        lmf.includes(query)
      );
    });

    return filteredUsers;
  }

  async communicationWithCurator(): Promise<void> {
    const curatorId = this.authUser?.curatorId;

    if (!curatorId) return;

    const [curator] = await lastValueFrom(this.getUsersData([curatorId]));

    this.openInternalMessengerChatWithUser(curator);
  }

  /**
   * Чтение профиля пользователя по идентификатору Телеграм
   * @param telegramId идентификатор записи
   */
  getUserByTelegramId(telegramId: number): Observable<UserProfileWithJobTitlesFull> {
    return this.userService.getListOfProfiles([telegramId]).pipe(
      catchError(err => throwError(() => err)),
      map(users => users[0]),
    );
  }

  openTelegramChatWithUser(user: UserUI) {
    if (this.env.isBot && user.phone) {
      this.telegramWebAppService.openTelegramLink(user.phone, true);
    } else if (user.telegramId) {
      this.router.navigate(['/chats', user.telegramId]);
    }
  }

  openInternalMessengerChatWithUser(user: UserUI) {
    this.internalMessengerService.openChatWithUser(user);
  }

  private getUserById(id: string, callback?: (value: boolean) => void): Observable<UserUI> {
    callback?.(true);

    return this.userService
      .getUserProfilesByIds([id])
      .pipe(
        catchError(err => {
          // TODO show error notification
          callback?.(false);
          return throwError(() => err);
        }),
        map(users => sortingUsers(users.map(user => normalizeUserDataForUI(user)))),
      )
      .pipe(
        map(users => {
          callback?.(false);
          return users[0];
        }),
      );
  }

  private getUsersByTelegramId(id: number, callback?: (value: boolean) => void): Observable<UserUI> {
    callback?.(true);

    return this.userService
      .getListOfProfiles([id])
      .pipe(
        catchError(err => {
          // TODO show error notification
          callback?.(false);
          return throwError(() => err);
        }),
        map(users => sortingUsers(users.map(user => normalizeUserDataForUI(user)))),
      )
      .pipe(
        map(users => {
          this.user$.next(users[0]);
          callback?.(false);
          return users[0];
        }),
      );
  }

  private getUsersByIds(ids: string[]): void {
    this.userService
      .getUserProfilesByIds(ids)
      .pipe(
        catchError(err => {
          // TODO show error notification
          return throwError(() => err);
        }),
        map(profiles => sortingUsers(profiles.map(profile => normalizeUserDataForUI(profile)))),
      )
      .subscribe(profiles => this.listOfProfiles$.next(profiles));
  }

  private getUsersByTelegramIds(ids: number[]): void {
    this.userService
      .getListOfProfiles(ids)
      .pipe(
        catchError(err => {
          // TODO show error notification
          return throwError(() => err);
        }),
        map(profiles => sortingUsers(profiles.map(profile => normalizeUserDataForUI(profile)))),
      )
      .subscribe(profiles => this.listOfProfiles$.next(profiles));
  }

  private sendError(message: string): void {
    this.alertService.error(message, {
      label: this.translateService.instant('common.alerts.errors.error2'),
      autoClose: 0,
    });
  }

  private sortingUserContacts(contacts: ContactForUserView[]): ContactForUserView[] {
    contacts
      ?.sort((a, b) => a.sortOrder! - b.sortOrder!)
      .map((contact, index) => {
        contact.sortOrder = (index + 1) * 10;
        return contact;
      });

    return contacts;
  }

  private showError(title: string, err: unknown) {
    const message = err instanceof Error ? err.message : err;
    this.alertService.error(`${title}\n${message}`);
  }
}
